import React, { useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  AcademicCapIcon,
  ArrowRightIcon,
  BookOpenIcon,
  CheckBadgeIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import type { SdleSalesIntent, SdleSalesLanguage } from "../utils/qbankCampaign";
import styles from "./QBankCampaignExperience.module.css";
import { publicSample as sample } from "../utils/publicQbankDemo";
import { trackTikTok } from "../utils/tiktok";
import { trackGa4 } from "../utils/analytics";

type Props = {
  onBrowseCollections: () => void;
  onUpgrade: () => void;
  onStartMockExam?: () => void;
  language?: SdleSalesLanguage;
  intent?: SdleSalesIntent;
};

const campaignCopy: Record<SdleSalesLanguage, {
  heroes: Record<SdleSalesIntent, { eyebrow: string; title: string; lede: string; primary: string; secondary: string }>;
  proofLabel: string;
  demoQuestions: string;
  answerReview: string;
  aiLabel: string;
  aiValue: string;
  studySystem: string;
  proofEyebrow: string;
  sampleTitle: string;
  publicSample: string;
  correct: string;
  correctAnswer: (answer: string) => string;
  reasoning: string;
  references: (count: number) => string;
  unlock: string;
  browseCollection: string;
}> = {
  en: {
    heroes: {
      preparation: {
        eyebrow: "Complete SDLE preparation",
        title: "The ultimate SDLE prep tool, built around real questions.",
        lede: "Study with referenced explanations, focused Question Bank collections, and mock exams in one clinical learning workspace.",
        primary: "Try a referenced question",
        secondary: "Browse exam collections",
      },
      question_bank: {
        eyebrow: "SDLE Question Bank",
        title: "An SDLE Question Bank That Explains the Answer.",
        lede: "Practice focused dental questions, review the reasoning, and inspect the available textbook or literature citations.",
        primary: "Try a referenced question",
        secondary: "Browse exam collections",
      },
      mock_exam: {
        eyebrow: "SDLE mock exams",
        title: "See Your Weak Areas Before the SDLE Does.",
        lede: "Take timed practice exams, review performance by specialty, and turn the result into a focused study plan.",
        primary: "Start an SDLE mock exam",
        secondary: "Try a referenced question",
      },
    },
    proofLabel: "C-Shaped SDLE study features",
    demoQuestions: "free practice questions",
    answerReview: "answer explanations and sources",
    aiLabel: "referenced follow-up answers",
    aiValue: "C-Shaped AI",
    studySystem: "one SDLE study system",
    proofEyebrow: "See the value before you subscribe",
    sampleTitle: "Try one complete referenced answer",
    publicSample: "Public sample",
    correct: "Correct — now understand why.",
    correctAnswer: (answer) => `The correct answer is ${answer}.`,
    reasoning: "Every explanation is designed to turn recall into clinical reasoning.",
    references: (count) => `${count} referenced ${count === 1 ? "source" : "sources"}`,
    unlock: "Unlock QBank + mock exams",
    browseCollection: "Browse SDLE collections",
  },
  ar: {
    heroes: {
      preparation: {
        eyebrow: "تحضير متكامل لاختبار SDLE",
        title: "استعد لاختبار SDLE بمنظومة واحدة.",
        lede: "تدرّب على أسئلة مركزة، وافهم الإجابات مع المراجع المتاحة، واستخدم الاختبارات التجريبية لتحديد نقاط ضعفك.",
        primary: "جرّب سؤالاً مع مرجع",
        secondary: "تصفح مجموعات الأسئلة",
      },
      question_bank: {
        eyebrow: "بنك أسئلة SDLE",
        title: "بنك أسئلة SDLE يشرح لك لماذا.",
        lede: "تدرّب على أسئلة طب الأسنان، وراجع منطق الإجابة، واطلع على الاستشهادات المتاحة من الكتب والمراجع العلمية.",
        primary: "جرّب سؤالاً مع مرجع",
        secondary: "تصفح مجموعات الأسئلة",
      },
      mock_exam: {
        eyebrow: "اختبارات SDLE التجريبية",
        title: "اكتشف نقاط ضعفك قبل يوم الاختبار.",
        lede: "تدرّب تحت ضغط الوقت، وراجع أداءك حسب التخصص، وحوّل النتيجة إلى خطة مذاكرة أكثر تركيزاً.",
        primary: "ابدأ اختبار SDLE تجريبياً",
        secondary: "جرّب سؤالاً مع مرجع",
      },
    },
    proofLabel: "مزايا التحضير في C-Shaped",
    demoQuestions: "أسئلة تدريبية مجانية",
    answerReview: "شرح الإجابات والمراجع",
    aiLabel: "إجابات متابعة موثقة بالمراجع",
    aiValue: "ذكاء C-Shaped",
    studySystem: "منظومة واحدة للتحضير",
    proofEyebrow: "جرّب القيمة قبل الاشتراك",
    sampleTitle: "جرّب إجابة كاملة مع المراجع",
    publicSample: "سؤال تجريبي",
    correct: "إجابة صحيحة — الآن افهم السبب.",
    correctAnswer: (answer) => `الإجابة الصحيحة هي ${answer}.`,
    reasoning: "صُمم كل شرح لتحويل التذكر إلى فهم سريري.",
    references: (count) => `${count} ${count === 1 ? "مرجع" : "مراجع"}`,
    unlock: "افتح بنك الأسئلة والاختبارات التجريبية",
    browseCollection: "تصفح مجموعات SDLE",
  },
};

const QBankCampaignExperience: React.FC<Props> = ({
  onBrowseCollections,
  onUpgrade,
  onStartMockExam,
  language = "en",
  intent = "preparation",
}) => {
  const sampleRef = useRef<HTMLElement | null>(null);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const copy = campaignCopy[language];
  const hero = copy.heroes[intent];
  const isArabic = language === "ar";

  const scrollToSample = () => {
    sampleRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const chooseOption = (label: string) => {
    if (selectedOption) return;
    setSelectedOption(label);
    trackTikTok("SampleQuestionViewed", "sdle", "sdle-public-sample");
    trackGa4("sample_question_answer", { bank: "sdle" }, { once: "sample_question_answer:sdle" });
  };

  const handlePrimaryAction = intent === "mock_exam" && onStartMockExam
    ? onStartMockExam
    : scrollToSample;
  const handleSecondaryAction = intent === "mock_exam"
    ? scrollToSample
    : onBrowseCollections;

  return (
    <div className={styles.campaignRoot} dir={isArabic ? "rtl" : "ltr"} lang={language}>
      <section className={styles.hero} aria-labelledby="sdle-campaign-title">
        <div className={styles.heroCopy}>
          <p className={styles.eyebrow}>{hero.eyebrow}</p>
          <h1 id="sdle-campaign-title">{hero.title}</h1>
          <p className={styles.lede}>{hero.lede}</p>
          <div className={styles.heroActions}>
            <button type="button" className={styles.primaryButton} onClick={handlePrimaryAction}>
              {hero.primary}
              <ArrowRightIcon aria-hidden="true" />
            </button>
            <button type="button" className={styles.secondaryButton} onClick={handleSecondaryAction}>
              {hero.secondary}
            </button>
          </div>
        </div>

        <aside className={styles.proofPanel} aria-label={copy.proofLabel}>
          <div className={styles.proofItem}>
            <AcademicCapIcon aria-hidden="true" />
            <div><strong>10</strong><span>{copy.demoQuestions}</span></div>
          </div>
          <div className={styles.proofItem}>
            <BookOpenIcon aria-hidden="true" />
            <div><strong>Q&A</strong><span>{copy.answerReview}</span></div>
          </div>
          <div className={styles.proofItem}>
            <ShieldCheckIcon aria-hidden="true" />
            <div><strong>{copy.aiValue}</strong><span>{copy.aiLabel}</span></div>
          </div>
          <div className={styles.proofItem}>
            <CheckBadgeIcon aria-hidden="true" />
            <div><strong>QBank + mocks</strong><span>{copy.studySystem}</span></div>
          </div>
        </aside>
      </section>

      <section ref={sampleRef} className={styles.sampleSection} aria-labelledby="sample-question-title">
        <div className={styles.sectionHeading}>
          <div>
            <p className={styles.eyebrow}>{copy.proofEyebrow}</p>
            <h2 id="sample-question-title">{copy.sampleTitle}</h2>
          </div>
        </div>

          <article className={styles.sampleCard}>
            <div className={styles.questionMeta}>
              <span>{sample.specialty}</span>
              <span>{copy.publicSample}</span>
            </div>
            <h3 dir="auto">{sample.question_text}</h3>
            <div className={styles.options}>
              {sample.options.map((option) => {
                const isCorrect = selectedOption !== null && option.label === sample.correct_answer;
                const isIncorrectSelection = selectedOption === option.label && option.label !== sample.correct_answer;
                return (
                  <button
                    type="button"
                    key={option.label}
                    className={`${styles.option} ${isCorrect ? styles.correctOption : ""} ${isIncorrectSelection ? styles.incorrectOption : ""}`}
                    onClick={() => chooseOption(option.label)}
                    disabled={selectedOption !== null}
                  >
                    <span>{option.label}</span>
                    <strong dir="auto">{option.text}</strong>
                  </button>
                );
              })}
            </div>

            {selectedOption && (
              <div className={styles.answerReveal} aria-live="polite">
                <div className={styles.answerResult}>
                  <ShieldCheckIcon aria-hidden="true" />
                  <div>
                    <strong>
                      {selectedOption === sample.correct_answer
                        ? copy.correct
                        : copy.correctAnswer(sample.correct_answer)}
                    </strong>
                    <span>{copy.reasoning}</span>
                  </div>
                </div>
                <div className={styles.explanation} dir="auto">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{sample.explanation}</ReactMarkdown>
                </div>
                {sample.references?.length > 0 && (
                  <div className={styles.references}>
                    <strong>{copy.references(sample.references.length)}</strong>
                    {sample.references.map(reference => (
                      <a key={reference.url} href={reference.url} target="_blank" rel="noreferrer">
                        {reference.title}
                      </a>
                    ))}
                  </div>
                )}
                <div className={styles.answerActions}>
                  <button type="button" className={styles.primaryButton} onClick={onUpgrade}>
                    {copy.unlock}
                    <ArrowRightIcon aria-hidden="true" />
                  </button>
                  <button type="button" className={styles.textButton} onClick={onBrowseCollections}>
                    {copy.browseCollection}
                  </button>
                </div>
              </div>
            )}
          </article>
      </section>
    </div>
  );
};

export default QBankCampaignExperience;
